import { useMemo } from 'react';
import { usePermissionCheck } from '../utils/permissions-utils';
import type { RoutePermissionConfig, FeatureType, PermissionModule, PermissionAction } from '../types/permissions';

/**
 * Hook principal para verificações de permissão em componentes
 */
export function usePermissions() {
  const { getCurrentPermissions, checkPermission, checkFeature, checkModule } = usePermissionCheck();

  // Memoiza as permissões para evitar recalcular a cada render
  const userPermissions = useMemo(() => getCurrentPermissions(), []);

  // Funções de conveniência para verificações comuns
  const permissions = useMemo(
    () => ({
      // Features
      hasMotorFeature: () => checkFeature('motor'),
      hasAppFeature: () => checkFeature('app'),
      hasScanFeature: () => checkFeature('scan'),

      // Motor Rules
      canViewMotorRules: () => checkModule('motorRules', 'view'),
      canCreateMotorRules: () => checkModule('motorRules', 'create'),
      canEditMotorRules: () => checkModule('motorRules', 'edit'),
      canDeleteMotorRules: () => checkModule('motorRules', 'delete'),

      // Conversion Rates
      canViewConversionRates: () => checkModule('motorConversionRates', 'view'),
      canCreateConversionRates: () => checkModule('motorConversionRates', 'create'),
      canEditConversionRates: () => checkModule('motorConversionRates', 'edit'),
      canDeleteConversionRates: () => checkModule('motorConversionRates', 'delete'),

      // Motor Variables
      canAccessMotorVariables: () => checkModule('motorVariables'),

      // Motor Giveaways
      canViewGiveaways: () => checkModule('motorGiveaways', 'view'),
      canCreateGiveaways: () => checkModule('motorGiveaways', 'create'),
      canEditGiveaways: () => checkModule('motorGiveaways', 'edit'),
      canDeleteGiveaways: () => checkModule('motorGiveaways', 'delete'),

      // Motor Campaigns
      canViewCampaigns: () => checkModule('motorCampaigns', 'view'),
      canCreateCampaigns: () => checkModule('motorCampaigns', 'create'),
      canEditCampaigns: () => checkModule('motorCampaigns', 'edit'),
      canDeleteCampaigns: () => checkModule('motorCampaigns', 'delete'),

      // Admin Users
      canViewAdminUsers: () => checkModule('adminUsers', 'view'),
      canCreateAdminUsers: () => checkModule('adminUsers', 'create'),
      canEditAdminUsers: () => checkModule('adminUsers', 'edit'),
      canDeleteAdminUsers: () => checkModule('adminUsers', 'delete'),

      // App Users
      canViewAppUsers: () => checkModule('appUsers', 'view'),
      canCreateAppUsers: () => checkModule('appUsers', 'create'),
      canEditAppUsers: () => checkModule('appUsers', 'edit'),
      canDeleteAppUsers: () => checkModule('appUsers', 'delete'),

      // Web App Users
      canViewWebAppUsers: () => checkModule('webappUsers', 'view'),
      canCreateWebAppUsers: () => checkModule('webappUsers', 'create'),
      canEditWebAppUsers: () => checkModule('webappUsers', 'edit'),
      canDeleteWebAppUsers: () => checkModule('webappUsers', 'delete'),

      // App Shops
      canViewShops: () => checkModule('appShops', 'view'),
      canCreateShops: () => checkModule('appShops', 'create'),
      canEditShops: () => checkModule('appShops', 'edit'),
      canDeleteShops: () => checkModule('appShops', 'delete'),

      // App Shops Categories
      canViewShopCategories: () => checkModule('appShopCategories', 'view'),
      canCreateShopCategories: () => checkModule('appShopCategories', 'create'),
      canEditShopCategories: () => checkModule('appShopCategories', 'edit'),
      canDeleteShopCategories: () => checkModule('appShopCategories', 'delete'),

      // App Terms of Use
      canViewTermsOfUse: () => checkModule('appTermsOfUse', 'view'),
      canCreateTermsOfUse: () => checkModule('appTermsOfUse', 'create'),
      canEditTermsOfUse: () => checkModule('appTermsOfUse', 'edit'),
      canDeleteTermsOfUse: () => checkModule('appTermsOfUse', 'delete'),

      // App Privacy Policies
      canViewPrivacyAndPolicy: () => checkModule('appPrivacyAndPolicy', 'view'),
      canCreatePrivacyAndPolicy: () => checkModule('appPrivacyAndPolicy', 'create'),
      canEditPrivacyAndPolicy: () => checkModule('appPrivacyAndPolicy', 'edit'),
      canDeletePrivacyAndPolicy: () => checkModule('appPrivacyAndPolicy', 'delete'),

      // App FAQs
      canViewFaqs: () => checkModule('appFaqs', 'view'),
      canCreateFaqs: () => checkModule('appFaqs', 'create'),
      canEditFaqs: () => checkModule('appFaqs', 'edit'),
      canDeleteFaqs: () => checkModule('appFaqs', 'delete'),

      // App FAQs Categories
      canViewFaqCategories: () => checkModule('appFaqCategories', 'view'),
      canCreateFaqCategories: () => checkModule('appFaqCategories', 'create'),
      canEditFaqCategories: () => checkModule('appFaqCategories', 'edit'),
      canDeleteFaqCategories: () => checkModule('appFaqCategories', 'delete'),

      // App Client Data (Shopping Settings)
      canViewShoppingData: () => checkModule('appClientData', 'view'),
      canEditShoppingData: () => checkModule('appClientData', 'edit'),

      // App Coupons
      canViewCoupons: () => checkModule('appCoupons', 'view'),
      canCreateCoupons: () => checkModule('appCoupons', 'create'),
      canEditCoupons: () => checkModule('appCoupons', 'edit'),
      canDeleteCoupons: () => checkModule('appCoupons', 'delete'),

      // App Products
      canViewProducts: () => checkModule('appProducts', 'view'),
      canCreateProducts: () => checkModule('appProducts', 'create'),
      canEditProducts: () => checkModule('appProducts', 'edit'),
      canDeleteProducts: () => checkModule('appProducts', 'delete'),

      // App Events
      canViewEvents: () => checkModule('appEvents', 'view'),
      canCreateEvents: () => checkModule('appEvents', 'create'),
      canEditEvents: () => checkModule('appEvents', 'edit'),
      canDeleteEvents: () => checkModule('appEvents', 'delete'),

      // App Benefit Redeems
      canViewBenefitRedeems: () => checkModule('appBenefitRedeems', 'view'),
      canCreateBenefitRedeems: () => checkModule('appBenefitRedeems', 'create'),
      canEditBenefitRedeems: () => checkModule('appBenefitRedeems', 'edit'),
      canDeleteBenefitRedeems: () => checkModule('appBenefitRedeems', 'delete'),

      // App Keep Up (Fique por dentro)
      canViewKeepUp: () => checkModule('appKeepUps', 'view'),
      canCreateKeepUp: () => checkModule('appKeepUps', 'create'),
      canEditKeepUp: () => checkModule('appKeepUps', 'edit'),
      canDeleteKeepUp: () => checkModule('appKeepUps', 'delete'),

      // App Highlights (Destaques)
      canViewHighlights: () => checkModule('appHighlights', 'view'),
      canCreateHighlights: () => checkModule('appHighlights', 'create'),
      canEditHighlights: () => checkModule('appHighlights', 'edit'),
      canDeleteHighlights: () => checkModule('appHighlights', 'delete'),

      // App Stories
      canViewStories: () => checkModule('appStories', 'view'),
      canCreateStories: () => checkModule('appStories', 'create'),
      canEditStories: () => checkModule('appStories', 'edit'),
      canDeleteStories: () => checkModule('appStories', 'delete'),

      // App Home
      canViewHome: () => checkModule('appHome', 'view'),
      canCreateHome: () => checkModule('appHome', 'create'),
      canEditHome: () => checkModule('appHome', 'edit'),
      canDeleteHome: () => checkModule('appHome', 'delete'),

      // App Banners (Ofertas)
      canViewBanners: () => checkModule('appBanners', 'view'),
      canCreateBanners: () => checkModule('appBanners', 'create'),
      canEditBanners: () => checkModule('appBanners', 'edit'),
      canDeleteBanners: () => checkModule('appBanners', 'delete'),

      // Verificação customizada
      checkCustomPermission: (config: RoutePermissionConfig) => checkPermission(config),

      // Verificação genérica
      checkGenericFeature: (feature: FeatureType) => checkFeature(feature),
      checkGenericModule: (module: PermissionModule, action?: PermissionAction) => checkModule(module, action),
    }),
    [checkPermission, checkFeature, checkModule]
  );

  return {
    userPermissions,
    ...permissions,
  };
}
